import { IconStar } from '@/app/components/atoms/Icons'
import { cx } from '@/app/lib/utils'
import { getRatingLabel, shouldShowRating } from '@/app/lib/ratings'

import styles from './RatingSummary.module.scss'
import type { RatingSummaryProps } from './types'

/**
 * `RatingSummary` — estrella + puntuación numérica + mensaje cualitativo
 * (spec.md FR-006/FR-009/FR-010/FR-011). Mismo maquetado con independencia de
 * si el origen del dato es el sistema propio (BD) o Bazaarvoice (research.md
 * R12) — el componente no sabe de dónde viene el número, solo lo pinta.
 *
 * Una única estrella (no cinco) en acento verde de marca, fiel al componente
 * de resumen del proyecto de diseño (Figma "Valoraciones del evento (N)" —
 * specs/046-valoraciones-bazaarvoice-bd): la estrella siempre va en acento,
 * con independencia de si más abajo el formulario de voto está habilitado o
 * deshabilitado (esa distinción solo afecta a las estrellas de selección, no
 * a este resumen).
 *
 * No renderiza nada si no hay valoraciones suficientes para el umbral del
 * entorno actual, o si falta la media (nunca se pinta un `0`/estrella vacía
 * como si fuese un dato real).
 */
export function RatingSummary({
  ratingSum,
  ratingAverage,
  threshold,
  inverse,
  compact,
  className,
}: RatingSummaryProps) {
  if (!shouldShowRating(ratingSum, threshold) || ratingAverage == null) return null

  const label = getRatingLabel(ratingAverage)

  return (
    <div className={cx(
        styles.summary,
        inverse && styles.inverse,
        compact && styles.compact,
        className,
      )}>
      <IconStar size={24} className={styles.star} />
      <span className={styles.average}>{ratingAverage.toFixed(1)}</span>

      {label && (
        <>
          <span className={styles.separator} aria-hidden="true">
            •
          </span>
          <span className={styles.label}>{label}</span>
        </>
      )}
    </div>
  )
}
